"use client";

import React, { useEffect, useState } from "react";
import { RefreshCw, WifiOff, ServerCrash } from "lucide-react";
import { cn } from "../utils";
import { TireSpinner } from "./TireSpinner";

export interface LoadingStateProps {
  /** Whether data is currently loading */
  loading: boolean;
  /** Error object (truthy = show error state) */
  error?: { message?: string } | string | null;
  /** Callback to retry the data fetch */
  onRetry?: () => void;
  /** Content to render once loading is complete and there's no error */
  children: React.ReactNode;
  /** Display variant */
  variant?: "fullscreen" | "inline" | "card";
  /** Custom loading message */
  loadingMessage?: string;
  /** Seconds before showing "still loading" hint (default: 8) */
  slowThreshold?: number;
  /** Seconds before showing retry button automatically (default: 25) */
  timeoutThreshold?: number;
  /** Additional class name for the container */
  className?: string;
  /** Size of the tire spinner */
  spinnerSize?: "sm" | "md" | "lg" | "xl";
}

/**
 * Unified loading & error state component for all RentOS apps.
 *
 * Handles three scenarios:
 * 1. Loading: Shows tire spinner with progressive messaging
 * 2. Error / Timeout: Shows error message + retry button
 * 3. Success: Renders children
 *
 * Progressive loading UX:
 * - 0–8s: Tire spinner + optional message
 * - 8–25s: "Server is waking up..." hint (free tier cold start)
 * - 25s+: Auto-shows retry button (even without an explicit error)
 *
 * @example
 * <LoadingState loading={loading} error={error} onRetry={refetch}>
 *   <YourContent />
 * </LoadingState>
 */
export function LoadingState({
  loading,
  error,
  onRetry,
  children,
  variant = "inline",
  loadingMessage,
  slowThreshold = 8,
  timeoutThreshold = 25,
  className,
  spinnerSize,
}: LoadingStateProps) {
  const [elapsed, setElapsed] = useState(0);

  // Track elapsed loading time
  useEffect(() => {
    if (!loading) {
      setElapsed(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [loading]);

  const hasTimedOut = loading && elapsed >= timeoutThreshold;
  const isSlow = loading && elapsed >= slowThreshold && elapsed < timeoutThreshold;

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message ?? "Something went wrong. Please try again.";

  // Determine spinner size based on variant
  const resolvedSize = spinnerSize ?? (variant === "fullscreen" ? "xl" : variant === "card" ? "md" : "lg");

  const containerStyles = {
    fullscreen:
      "fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm",
    inline: "flex flex-col items-center justify-center min-h-[60vh] py-16 px-4",
    card: "flex flex-col items-center justify-center py-10 px-4",
  };

  // Error or timeout state
  if (error || hasTimedOut) {
    return (
      <div className={cn(containerStyles[variant], className)}>
        <div className="flex flex-col items-center text-center max-w-sm">
          {/* Icon */}
          <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-destructive/10">
            {error ? (
              <ServerCrash className="h-8 w-8 text-destructive" />
            ) : (
              <WifiOff className="h-8 w-8 text-muted-foreground" />
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {error ? "Failed to load data" : "Server is taking too long"}
          </h3>

          {/* Description */}
          <p className="text-sm text-muted-foreground mb-6">
            {error
              ? errorMessage
              : "This is taking longer than expected. Please try again."}
          </p>

          {/* Retry button */}
          {onRetry && (
            <button
              onClick={() => {
                setElapsed(0);
                onRetry();
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className={cn(containerStyles[variant], className)}>
        <TireSpinner
          size={resolvedSize}
          label={
            isSlow
              ? "Still loading, hang tight..."
              : loadingMessage ?? "Loading..."
          }
        />
        {isSlow && (
          <p className="mt-2 text-xs text-muted-foreground/70">
            This may take a few seconds
          </p>
        )}
      </div>
    );
  }

  // Content ready
  return <>{children}</>;
}
